import React, { useState, useCallback, useMemo } from 'react'
import { Box, Button, TextField, Table, TableHead, TableBody, TableRow, TableCell, Tooltip } from '@material-ui/core'
import { useStyles } from './styles'
import {
  DEFAULT_REQUEST_BODY,
  CSV_FILE_PREFIX,
  DRILL_CSV_PREFIX,
  EMPTY_TABLE_TEXT,
  FETCH_ERROR_MESSAGE,
  SCORE_THRESHOLDS,
  DRILL_HEADINGS,
} from './constants'
import { getErrorMessage } from '../constants'
import TableData from '../TableData'
import Search from '../../../../elements/icons/Search'
import Download from '../../../../elements/icons/Download'
import { DisplayMessage, Loader } from '../../../../Components'
import { eventService } from '../../../../Services/EventService'
import { connect } from 'react-redux'
import * as messageTypes from '../../../../utils/messageTypes'

const formatEpochToTimestamp = (epoch) => {
  if (epoch == null) return ''
  const date = new Date(epoch * 1000)
  return date.toISOString().replace('T', ' ').slice(0, 19) + ' UTC'
}

const getScoreClass = (classes, score) => {
  if (score == null) return classes.scoreNull
  if (score >= SCORE_THRESHOLDS.GREAT) return classes.scoreGreat
  if (score >= SCORE_THRESHOLDS.GOOD) return classes.scoreGood
  if (score >= SCORE_THRESHOLDS.WARN) return classes.scoreWarn
  return classes.scoreBad
}

const escapeCsvValue = (value) => {
  const stringValue = value == null ? '' : String(value)
  return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue
}

const SiteDailyPerformance = () => {
  const classes = useStyles()

  const [allGroups, setAllGroups] = useState([])
  const [dateCols, setDateCols] = useState([])
  const [metaData, setMetaData] = useState({ serverTimeStamp: '', rowCount: 0 })
  const [reqBody, setReqBody] = useState(DEFAULT_REQUEST_BODY)
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [expandedDrills, setExpandedDrills] = useState({})
  const [hiddenPerfectRows, setHiddenPerfectRows] = useState({})
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const processResponse = useCallback((response) => {
    const meta = response.meta ?? {}
    setMetaData({
      serverTimeStamp: meta.serverTimeStamp ?? '',
      rowCount: meta.rowCount ?? (response.data?.length ?? 0),
    })

    const records = response.data ?? []
    const dateSet = new Set()
    const groupMap = {}

    records.forEach(record => {
      const compositeKey = `${record.siteId}||${record.programkey}`
      if (!groupMap[compositeKey]) {
        groupMap[compositeKey] = {
          siteId: record.siteId,
          programkey: record.programkey,
          byDate: {},
        }
      }
      groupMap[compositeKey].byDate[record.performanceDate] = record
      dateSet.add(record.performanceDate)
    })

    const sortedDates = [...dateSet].sort()
    setDateCols(sortedDates)
    setAllGroups(Object.values(groupMap))
    setExpandedDrills({})
    setHiddenPerfectRows({})
    setSearchTerm('')
    setPage(0)
  }, [])

  const fetchData = () => {
    let body
    try {
      body = JSON.parse(reqBody.trim())
    } catch (err) {
      setMessage('Invalid JSON in Request Body')
      setMessageType(messageTypes.ERROR)
      setOpen(true)
      return
    }

    setLoading(true)
    eventService.getSiteDailyPerformance(body)
      .then(data => {
        processResponse(data)
        setMessage(`${data.data?.length ?? 0} record(s) loaded`)
        setMessageType(messageTypes.SUCCESS)
        setOpen(true)
      })
      .catch(error => {
        setMessage(getErrorMessage(error, FETCH_ERROR_MESSAGE))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() => setLoading(false))
  }

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return allGroups
    const lowerSearch = searchTerm.trim().toLowerCase()
    return allGroups.filter(group =>
      String(group.siteId).toLowerCase().includes(lowerSearch)
    )
  }, [allGroups, searchTerm])

  // Build table headings: #, Site ID, Program Key, ...dates
  const tableHeadings = useMemo(() => [
    { id: 'index', label: '#', sortOrder: null },
    { id: 'siteId', label: 'Site ID', sortOrder: null },
    { id: 'programkey', label: 'Program Key', sortOrder: null },
    ...dateCols.map(dateCol => ({
      id: `date_${dateCol}`,
      label: dateCol,
      sortOrder: null,
    })),
  ], [dateCols])

  // Build table contents
  const tableContents = useMemo(() =>
    filteredGroups.map((group, index) => ({
      ...group,
      id: `${group.siteId}_${group.programkey}`,
      _idx: index + 1,
    })),
    [filteredGroups]
  )

  const toggleDrillDown = (rowIndex, dateCol) => {
    setExpandedDrills(prev => {
      const currentDate = prev[rowIndex]
      if (currentDate === dateCol) {
        const next = { ...prev }
        delete next[rowIndex]
        return next
      }
      return { ...prev, [rowIndex]: dateCol }
    })
  }

  const toggleHidePerfectRows = (drillKey) => {
    setHiddenPerfectRows(prev => ({
      ...prev,
      [drillKey]: !prev[drillKey],
    }))
  }

  const renderCellContent = (item, heading) => {
    if (heading.id === 'index') return item._idx

    if (heading.id === 'siteId') {
      return <Box component="span" className={classes.chipBlue}>{item.siteId}</Box>
    }

    if (heading.id === 'programkey') {
      return (
        <Box component="span" className={classes.chipPurple}>
          {item.programkey || '\u2014'}
        </Box>
      )
    }

    // Date columns
    if (heading.id.startsWith('date_')) {
      const dateCol = heading.id.replace('date_', '')
      const record = item.byDate?.[dateCol]
      if (!record) {
        return <Box component="span" className={classes.missingDate}>{'\u2014'}</Box>
      }

      const scoreClass = getScoreClass(classes, record.dailyScore)
      const scoreText = record.dailyScore != null ? record.dailyScore.toFixed(2) : '\u2014'
      const eventCount = record.dailyEventCount ?? 0
      const hasFailure = !!record.failureReason

      const rowIndex = tableContents.findIndex(row => row.id === item.id)
      const isExpanded = expandedDrills[rowIndex] === dateCol

      return (
        <Box className={classes.dateCellRow}>
          <Box component="span" className={scoreClass}>{scoreText}</Box>
          {hasFailure ? (
            <Tooltip title={`Failure: ${record.failureReason}`} arrow placement="top">
              <Box component="span" className={`${classes.eventCountChip} ${classes.eventCountFailure}`}>
                {eventCount}
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="Daily Event Count" arrow placement="top">
              <Box component="span" className={classes.eventCountChip}>
                {eventCount}
              </Box>
            </Tooltip>
          )}
          <Button
            className={`${classes.drillBtn} ${isExpanded ? classes.drillBtnOpen : ''}`}
            onClick={() => toggleDrillDown(rowIndex, dateCol)}
          >
            &#9660;
          </Button>
        </Box>
      )
    }

    return ''
  }

  const getCellClassName = (item, heading) => {
    if (heading.id === 'index') return classes.indexCell
    if (heading.id.startsWith('date_')) return classes.titleWrapper
    return classes.titleWrapper
  }

  // Build drill-down sub-table for a specific date
  const buildDrillPanel = (group, dateCol) => {
    const record = group.byDate?.[dateCol]
    if (!record) return null

    const performanceList = record.performanceList || []
    const drillKey = `${group.siteId}_${dateCol}`
    const hidePerfect = hiddenPerfectRows[drillKey]
    const hasPerfectRows = performanceList.some(interval => interval.percentage === 100)

    const scoreClass = getScoreClass(classes, record.dailyScore)
    const scoreText = record.dailyScore != null ? record.dailyScore.toFixed(2) : '\u2014'

    return (
      <Box className={classes.expandInner}>
        <Box className={classes.expandHeader}>
          <Box component="span" className={classes.expandTitle}>{dateCol}</Box>
          <Box component="span" className={scoreClass}>Score: {scoreText}</Box>
          <Box component="span" className={classes.expandMeta}>
            {performanceList.length} intervals
          </Box>
          <Box className={classes.expandActions}>
            {hasPerfectRows && (
              <Button
                className={classes.clearBtn}
                onClick={() => toggleHidePerfectRows(drillKey)}
              >
                {hidePerfect ? 'Show 100% rows' : 'Hide 100% rows'}
              </Button>
            )}
            <Button
              className={classes.exportBtn}
              onClick={() => exportDrillCsv(group.siteId, dateCol, performanceList)}
            >
              <Download />
              <Box paddingLeft="5px">Export CSV</Box>
            </Button>
          </Box>
        </Box>
        <Box style={{ overflowX: 'auto' }}>
          <Table className={classes.drillTable}>
            <TableHead>
              <TableRow>
                {DRILL_HEADINGS.map(heading => (
                  <TableCell key={heading.id}>{heading.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {performanceList.map((interval, intervalIndex) => {
                const isPerfect = interval.percentage === 100
                if (hidePerfect && isPerfect) return null
                return (
                  <TableRow key={intervalIndex}>
                    <TableCell>{formatEpochToTimestamp(interval.endAt)}</TableCell>
                    <TableCell>
                      {interval.dischargePower != null
                        ? interval.dischargePower.toLocaleString()
                        : '\u2014'}
                    </TableCell>
                    <TableCell>
                      {interval.rateWatt != null
                        ? interval.rateWatt.toLocaleString()
                        : '\u2014'}
                    </TableCell>
                    <TableCell className={isPerfect ? classes.perfPerfect : ''}>
                      {interval.percentage != null
                        ? `${interval.percentage.toFixed(2)}%`
                        : '\u2014'}
                    </TableCell>
                    <TableCell>
                      {interval.soc != null ? `${interval.soc}%` : '\u2014'}
                    </TableCell>
                    <TableCell>
                      {interval.targetSoc != null ? `${interval.targetSoc}%` : ''}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    )
  }

  const renderExpandedRow = (item, index, colSpan) => {
    const expandedDate = expandedDrills[index]
    if (!expandedDate) return null

    return (
      <TableRow key={`expand_${item.id}`} className={classes.expandRow}>
        <TableCell colSpan={colSpan} style={{ padding: 0 }}>
          {buildDrillPanel(item, expandedDate)}
        </TableCell>
      </TableRow>
    )
  }

  // Main CSV export
  const exportMainCsv = () => {
    if (!filteredGroups.length) return

    const header = ['Site ID', 'Program Key',
      ...dateCols.flatMap(dateCol => [`${dateCol} Score`, `${dateCol} Events`]),
    ].map(escapeCsvValue).join(',')

    const csvRows = filteredGroups.map(group => {
      const dateValues = dateCols.flatMap(dateCol => {
        const record = group.byDate?.[dateCol]
        return [
          record?.dailyScore != null ? record.dailyScore : '',
          record?.dailyEventCount != null ? record.dailyEventCount : '',
        ]
      })
      return [group.siteId, escapeCsvValue(group.programkey ?? ''), ...dateValues].join(',')
    })

    const csvContent = [header, ...csvRows].join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(blob)
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `${CSV_FILE_PREFIX}_${timestamp}.csv`
    link.click()
    URL.revokeObjectURL(downloadUrl)
  }

  // Drill-down CSV export
  const exportDrillCsv = (siteId, dateCol, performanceList) => {
    const header = DRILL_HEADINGS.map(heading => escapeCsvValue(heading.label)).join(',')
    const csvRows = performanceList.map(interval =>
      [
        escapeCsvValue(formatEpochToTimestamp(interval.endAt)),
        interval.dischargePower != null ? interval.dischargePower : '',
        interval.rateWatt != null ? interval.rateWatt : '',
        interval.percentage != null ? interval.percentage.toFixed(2) + '%' : '',
        interval.soc != null ? interval.soc + '%' : '',
        interval.targetSoc != null ? interval.targetSoc + '%' : '',
      ].join(',')
    )

    const csvContent = [header, ...csvRows].join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `${DRILL_CSV_PREFIX}_${siteId}_${dateCol}.csv`
    link.click()
    URL.revokeObjectURL(downloadUrl)
  }

  return (
    <Box>
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
      <Loader loading={loading} />

      <Box className={classes.urlBar}>
        <Box className={classes.urlRow}>
          <Box className={classes.urlBarLabel}>Request Body (JSON)</Box>
          <Button className={classes.submitBtn} onClick={fetchData} disabled={loading}>
            <Search />
            <Box paddingLeft="10px">Fetch &amp; Render</Box>
          </Button>
        </Box>
        <TextField
          className={classes.reqTextarea}
          value={reqBody}
          onChange={(e) => setReqBody(e.target.value)}
          multiline
          minRows={5}
          variant="standard"
          InputProps={{ disableUnderline: true, style: { color: '#e2e8f0', padding: '8px 12px' } }}
        />
      </Box>

      {allGroups.length > 0 && (
        <Box className={classes.statsBar}>
          <Box component="span" className={classes.statPill}>
            <Box component="span" className={classes.statPillStrong}>Server Time:</Box>
            &nbsp;{metaData.serverTimeStamp || '\u2014'}
          </Box>
          <Box component="span" className={`${classes.statPill} ${classes.statPillGreen}`}>
            <Box component="span" className={classes.statPillStrong}>Row Count:</Box>
            &nbsp;{metaData.rowCount}
          </Box>
        </Box>
      )}

      <Box className={classes.container}>
        <Box className={classes.card}>
          <Box className={classes.filterBar}>
            <TextField
              className={classes.filterInput}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }}
              placeholder="Search Site ID..."
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <Button className={classes.clearBtn} onClick={() => { setSearchTerm(''); setPage(0) }}>
              Clear
            </Button>

            <Box className={classes.filterRight}>
              <Box component="span" className={classes.resultCount}>
                {filteredGroups.length} of {allGroups.length} sites
              </Box>
              <Button className={classes.exportBtn} onClick={exportMainCsv}>
                <Download />
                <Box paddingLeft="5px">Export CSV</Box>
              </Button>
            </Box>
          </Box>

          <TableData
            classes={classes}
            tableHeadings={tableHeadings}
            tableContents={tableContents}
            isBold=""
            renderCellContent={renderCellContent}
            getCellClassName={getCellClassName}
            renderExpandedRow={renderExpandedRow}
            containerClassName={classes.tableWrap}
            tableClassName={classes.perfTable}
            stickyHeader={false}
            emptyTableText={EMPTY_TABLE_TEXT}
            paginate
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser } = auth
  return { currentUser }
}

export default connect(mapStateToProps)(SiteDailyPerformance)
